/**
 * force-directed-graph.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, EventEmitter, HostListener, Input, OnInit, Output }
  from "@angular/core";
import * as D3Force from "d3-force";
import * as D3Scale from "d3-scale";

import { identity } from "../support/utilities/functions";

import { Graph, GraphEdge, GraphNode, StandardGraph } from "./graph";

const DEFAULT_LINK_LENGTH : number = 70;
const DEFAULT_LINK_STRENGTH : number = 0.5;
const DEFAULT_CHARGE_STRENGTH : number = -90;
const PAN_SPEED_FACTOR : number = 2.0;
const BOUNDING_FORCE_PADDING : number = 20;
const BOUNDING_FORCE : number = 0.5;

const REVERSED_SCALE_10 : string[] =
  D3Scale.schemeCategory10.map(identity).reverse();

interface PhysicsPoint
{
    id : number;
    category : string;
    x : number;
    y : number;
    vx : number;
    vy : number;
    fx? : number;
    fy? : number;
}

interface PhysicsLink
{
  source : PhysicsPoint;
  target : PhysicsPoint;
}

const DEAD_POINT : PhysicsPoint = {
  id: -1,
  category: "",
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  fx: 0,
  fy: 0
};

type GraphSim = D3Force.Simulation<PhysicsPoint, PhysicsLink>;

/**
 * A behaviorless component used as an endpoint for the {@link LogoutGuard}.
 */
@Component({
  selector: `force-directed-graph`,
  template: `
  <!-- Note that if we don't use <svg> as the outermost tag, the angular schema
  parser may not automatically recognize the namespace. In that case we should
  explicitly declare the namespaces of tags, like so: <svg:line> -->
  <svg
    #svgCanvas
    [attr.viewbox]="'0 0 ' + width + ' ' + height"
    [style.width]="width + 'px'"
    [style.height]="height + 'px'"
    preserveAspectRatio="xMidYMin meet"
    (wheel)="zoom($event)"
    (mousedown)="startDrag($event)"
    (mousemove)="updateDrag($event)"
    (mouseup)="endDrag()"
    (mouseleave)="endDrag()">
    <defs>
      <marker
        id="arrowhead"
        markerWidth="12"
        markerHeight="6"
        refX="24"
        refY="3"
        orient="auto"
        markerUnits="strokeWidth">
        <path d="M0,0 L0,6 L12,3 z"></path>
      </marker>
    </defs>
    <g
      class="graph"
      [style.transform]="zoomTransform">
      <g class="edges">
        <g *ngFor="let edge of _graph?.edges; let i = index">
          <template [ngIf]="edge.enabled">
            <line
              [class.standard]="!_graph?.categorizeEdges"
              [attr.x1]="physical(edge.source).x"
              [attr.y1]="physical(edge.source).y"
              [attr.x2]="physical(edge.target).x"
              [attr.y2]="physical(edge.target).y"
              [attr.marker-end]="_graph.directed ? 'url(#arrowhead)' : ''"
              [attr.stroke]="color(edge.category, true)">
            </line>
            <template [ngIf]="edge.hasSecondaries">
              <line *ngFor="let alt of edge.secondaries; let j = index"
                [attr.x1]="physical(edge.source).x"
                [attr.y1]="physical(edge.source).y"
                [attr.x2]="physical(edge.target).x"
                [attr.y2]="physical(edge.target).y"
                [attr.marker-end]="_graph.directed ? 'url(#arrowhead)' : ''"
                [attr.stroke]="color(alt.category, true)"
                [attr.stroke-dasharray]="'5 ' + (5*j+5)">
              </line>
            </template>
            <g class="edgelabel">
              <path
                [attr.d]="asPath(edge)"
                [id]="'edgepath' + i"
                stroke-color="green">
              </path>
              <text
                *ngFor="let startLabel of labelsForPath(edge, true); let j = index; let first = first"
                dx="16"
                [attr.dy]="-8 - (12 * j)">
                <textPath [attr.href]="'#edgepath' + i">
                  {{startLabel}}{{first ? "" : ","}}
                </textPath>
              </text>
              <text
                *ngFor="let endLabel of labelsForPath(edge, false); let j = index; let last = last"
                dx="-16"
                [attr.dy]="16 + (12 * j)"
                text-anchor="end">
                <textPath [attr.href]="'#edgepath' + i" startOffset="100%">
                  {{endLabel}}{{last ? "" : ","}}
                </textPath>
              </text>
            </g>
          </template>
        </g>
      </g>

      <g class="nodes">
        <g
          *ngFor="let node of _graph?.nodes"
          [class.highlight]="highlight === node.id"
          [title]="node.label"
          (mouseenter)="selected.emit(node.id)"
          (mouseleave)="selected.emit(null)"
          (click)="expand.emit(node); restartSim()">
          <template [ngIf]="node.enabled">
            <circle
              r="12"
              [attr.cx]="physical(node).x"
              [attr.cy]="physical(node).y"
              [attr.title]="node.id"
              [attr.stroke]="color ? color(node.category) : 'red'"
              fill="white">
            </circle>
            <circle
              *ngIf="node.alreadyExpanded === false"
              r="18"
              [attr.cx]="physical(node).x || -10"
              [attr.cy]="physical(node).y || -10"
              stroke="#999"
              stroke-dasharray="1 3"
              fill="transparent"
              title="This node may have edges that haven't been loaded">
            </circle>
            <text
              *ngIf="showLabels"
              class="node-label"
              [attr.x]="physical(node).x"
              [attr.y]="physical(node).y + 25"
              [attr.fill]="color ? color(node.category) : 'red'"
              text-anchor="middle">
              {{node.label}}
            </text>
            <text
              class="node-id"
              [attr.x]="physical(node).x"
              [attr.y]="physical(node).y + 5"
              [attr.fill]="color ? color(node.category) : 'red'"
              text-anchor="middle">
              {{node.id}}
            </text>
          </template>
        </g> <!-- /node -->
      </g> <!-- /nodes -->
    </g> <!-- /graph -->
    <!-- this rect is a sanity test about viewbox / bounding manipulations -->
    <rect
      fill="none"
      stroke="red"
      stroke-dasharray="5,5"
      x="-2"
      y="-2"
      [attr.width]="width + 4"
      [attr.height]="height + 4">
    </rect>
    <text
      *ngIf="_graph?.nodes.length === 1"
      [attr.x]="width / 2"
      [attr.y]="height - 15"
      text-anchor="middle"
      fill="gray">
      The server could not find any explanation edges.
    </text>
    <text
      [attr.x]="width"
      y="12"
      text-anchor="end"
      font-size="10"
      fill="gray">
      (Zoom with the mouse wheel or ± keys.
        Pan with arrow keys or click-and-drag.)
    </text>
  </svg>
  `,
  styles: [`
    svg {
      display: block;
      max-width: 100%;
      margin: auto;
      /* border: 1px solid black; */
    }
    .graph {
      transform-origin: center;
    }
    .edges line.standard {
      stroke: #999;
      stroke-opacity: 0.6;
    }
    #arrowhead {
      fill: #999;
      fill-opacity: 0.6;
    }
    .nodes circle {
      /* stroke: #fff; */
      stroke-width: 2px;
    }
    .node-label {
      text-shadow: 0 0 3px white;
    }
    .highlight circle {
      stroke: black;
      filter: drop-shadow(0 0 3px yellow);
    }
    .highlight text {
      fill: black;
      /* text-shadow: 0 0 2px yellow; */
      font-weight: bold;
    }
    text {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  `]
})
export class ForceDirectedGraphComponent
{
  @Input()
  public showLabels : boolean = false;

  private _graph : StandardGraph;
  @Input() public set graph (g : StandardGraph)
  {
    if (g)
    {
      console.log(this);
      this._graph = g;
      this.initializeGraph();
    }
  }

  @Input()
  public highlight : number | null = null;

  @Output()
  public selected : EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public expand : EventEmitter<GraphNode> = new EventEmitter<GraphNode>();

  @Output("simulation")
  public simulationEmitter : EventEmitter<GraphSim> = new EventEmitter();

  public simulatedNodes : GraphNode[] = [];
  public simulatedEdges : GraphEdge[] = [];

  private colorScale : D3Scale.ScaleOrdinal<string, string>;
  private colorScaleAlternate : D3Scale.ScaleOrdinal<string, string>;
  public color (input : string, alternate : boolean = false) : string
  {
    if (input && this.colorScale && this.colorScaleAlternate)
    {
      return (alternate
        ? this.colorScaleAlternate(input)
        : this.colorScale(input));
    }
    return "black";
  }

  public labelsForPath (edge : GraphEdge, forPathStart : boolean) : string[]
  {
    const [source, target] = this.physical(edge);
    const inverted : boolean = this.hasInvertedTextPath(source, target);
    if (forPathStart !== inverted)
    {
      // We are either in regular orientation and on the path start side or in
      // inverted orientation and on the path end side, hence we need the
      // source role(s) for the edge.
      return edge.labelsWithSecondaries;
    }
    else
    {
      // We are either in inverted orientation and on the path start side or in
      // regular orientation and on the path end side, hence we need the
      // target role(s) for the edge.
      return edge.reverseLabelsWithSecondaries;
    }
  }

  public hasInvertedTextPath (source : PhysicsPoint, target : PhysicsPoint)
    : boolean
  {
    return (target.x < source.x);
  }

  public asPath (edge : GraphEdge) : string
  {
    const [source, target] = this.physical(edge);
    if (this.hasInvertedTextPath(source, target))
    {
      return (
        `M ${target.x} ${target.y} `
        + `L ${source.x} ${source.y}`);
    }
    else
    {
      return (
        `M ${source.x} ${source.y} `
        + `L ${target.x} ${target.y}`);
    }
  }

  private linkDistance () : number
  {
    return (this._graph.idealLinkLength || DEFAULT_LINK_LENGTH);
  }
  private linkStrength () : number
  {
    return (this._graph.linkStrength || DEFAULT_LINK_STRENGTH);
  }
  private chargeStrength () : number
  {
    return (this._graph.chargeStrength || DEFAULT_CHARGE_STRENGTH);
  }

  /**
   * A weak map to hold the correspondence between objects and their simulated
   * physical representations.
   */
  public physics : WeakMap<GraphNode, PhysicsPoint> = new WeakMap();

  public physical (node : GraphNode) : PhysicsPoint;
  public physical (edge : GraphEdge) : [PhysicsPoint, PhysicsPoint];
  public physical (
    item : GraphNode | GraphEdge
  ) : PhysicsPoint | [PhysicsPoint, PhysicsPoint]
  {
    if (!item)
    {
      console.error(
        "Serious graph failure: requested physics of non-object", item);
      return DEAD_POINT;
    }
    if ("source" in item && "target" in item)
    {
      const edge : GraphEdge = item as GraphEdge;
      return [
        this.physical(edge.source),
        this.physical(edge.target)
      ];
    }
    else if ("id" in item)
    {
      const node : GraphNode = item as GraphNode;
      let pt : PhysicsPoint = this.physics.get(node);
      if (!pt)
      {
        pt = this.initPoint(node);
        this.physics.set(node, pt);
      }
      return pt;
    }
    else
    {
      console.error(
        "Serious graph failure: requested physics of unknown object", item);
      return DEAD_POINT;
    }
  }
  private initPoint (node : GraphNode) : PhysicsPoint
  {
    return {
      id: node.id,
      category: node.category,
      x: this.centerX,
      y: this.centerY,
      vx: 0,
      vy: 0
    };
  }

  public simulation : D3Force.Simulation<PhysicsPoint, PhysicsLink>;

  @Input()
  public width : number = 600;

  @Input()
  public height : number = 300;

  private centerX : number = this.width / 2;
  private centerY : number = this.height / 2;

  private updateCentering () : void
  {
    if (this.simulation)
    {
      this.simulation.force(
        "center",
        D3Force.forceCenter(this.centerX, this.centerY));
    }
  }
  private holdForInteraction () : void
  {
    this.simulation.alphaTarget(0.5).restart();
  }
  private finishInteraction () : void
  {
    this.simulation.alphaTarget(0);
  }
  public restartSim () : void
  {
    this.simulation.alpha(1.0).restart();
  }

  @HostListener("document:keydown.ArrowLeft", ["-1", "0"])
  @HostListener("document:keydown.ArrowRight", ["1", "0"])
  @HostListener("document:keydown.ArrowUp", ["0", "-1"])
  @HostListener("document:keydown.ArrowDown", ["0", "1"])
  public pan (dx : number = 0, dy : number = 0) : void
  {
    this.restartSim();
    this.centerX += dx * PAN_SPEED_FACTOR;
    this.centerY += dy * PAN_SPEED_FACTOR;
    this.updateCentering();
  }
  public panTo (x : number, y : number) : void
  {
    this.centerX = x;
    this.centerY = y;
    this.updateCentering();
  }

  private dragStart : {x : number, y : number};
  private preDragCenter : {x : number, y : number};
  private isDragging : boolean = false;
  public startDrag (event : MouseEvent) : void
  {
    this.dragStart = event;
    this.preDragCenter = {x : this.centerX, y : this.centerY};
    this.isDragging = true;
    this.holdForInteraction();
  }
  public endDrag () : void
  {
    this.isDragging = false;
    this.finishInteraction();
  }
  public updateDrag (event : MouseEvent) : void
  {
    if (this.isDragging)
    {
      this.panTo(
        (event.x - this.dragStart.x + this.preDragCenter.x),
        (event.y - this.dragStart.y + this.preDragCenter.y)
      );
    }
  }

  @Input()
  public coolingRate : number = 0.01;

  private initializeGraph () : void
  {
    console.log("initializing simulation");
    this.simulatedNodes = this._graph.nodes.filter((n : GraphNode) => {
      if (n.enabled)
      {
        n.simulated = true;
        return true;
      }
      return false;
    });
    const physicalNodes : PhysicsPoint[] = this.simulatedNodes.map(
      (n : GraphNode) => this.physical(n));

    this.simulatedEdges = this._graph.edges.filter((e : GraphEdge) => {
      if (e.enabled)
      {
        e.simulated = true;
        return true;
      }
      return false;
    });
    const physicalEdges : PhysicsLink[] = this.simulatedEdges.map(
      (e : GraphEdge) =>
        ({
          source: this.physical(e.source),
          target: this.physical(e.target)
        }));

    this.colorScale = D3Scale.scaleOrdinal<string>(D3Scale.schemeCategory10);
    this.colorScaleAlternate = D3Scale.scaleOrdinal<string>(REVERSED_SCALE_10);

    const linkingForce : D3Force.ForceLink<PhysicsPoint, PhysicsLink> =
      D3Force.forceLink<PhysicsPoint, PhysicsLink>(physicalEdges)
      .distance(() => this.linkDistance())
      .strength((link : GraphEdge) => this.linkStrength());

    const boundingBoxForce : D3Force.Force<PhysicsPoint, PhysicsLink> =
      (alpha : number) => physicalNodes.forEach((p : PhysicsPoint) => {
        if (p.x > (this.width - BOUNDING_FORCE_PADDING))
        {
          p.vx -= BOUNDING_FORCE;
        }
        if (p.x < (BOUNDING_FORCE_PADDING))
        {
          p.vx += BOUNDING_FORCE;
        }
        if (p.y > (this.height - BOUNDING_FORCE_PADDING))
        {
          p.vy -= BOUNDING_FORCE;
        }
        if (p.y < (BOUNDING_FORCE_PADDING))
        {
          p.vy += BOUNDING_FORCE;
        }
      });

    // TODO: this force needs to be generalized somehow
    const directedness : D3Force.Force<PhysicsPoint, PhysicsLink> =
      (a : number) => {
        const minDisplacement : number = 40;
        const scaling : number = 0.2;
        physicalEdges.forEach((edge : GraphEdge) => {
          if (typeof edge.target !== "object"
              || typeof edge.source !== "object")
          {
            // I'm not going to reimplement the forceLink id logic, so just bail
            // out if the edges aren't already initialized to
            return;
          }
          const stretch : number =
            edge.target.x - edge.source.x - minDisplacement;
          if (stretch < -1)
          {
            // If the target is less than minDisplacement
            edge.source.vx += (stretch * scaling);
            edge.target.vx -= (stretch * scaling);
          }
        });
      };

    this.simulation = D3Force.forceSimulation(physicalNodes)
        .force("link", linkingForce)
        .force("charge", D3Force.forceManyBody().strength(
          () => this.chargeStrength()))
        .force("box", boundingBoxForce)
        .alphaDecay(this.coolingRate);
    this.pan(); // will apply centering force
    if (this._graph.directed)
    {
      // only apply the direction-sorting force if the graph is directed
      this.simulation.force("hierarchy", directedness);
    }

    this.simulation.on("tick", () => {
      if (this._graph.reinitialize)
      {
        this.simulation.stop();
        this._graph.reinitialize = false;
        this.initializeGraph();
      }
      // use this area for debugging force / simulation processes
    });
    this.simulationEmitter.emit(this.simulation);
  }

  private zoomExp : number = 0;
  public zoomTransform : string = "";
  private changeZoom (delta : number) : void
  {
    const [zoomMin, zoomMax] : [number, number] = [-1.5, 1.2];
    this.zoomExp -= delta;
    // Constrain to zoom limits
    if (this.zoomExp < zoomMin)
    {
      this.zoomExp = zoomMin;
    }
    if (this.zoomExp > zoomMax)
    {
      this.zoomExp = zoomMax;
    }
    // Apply the transformation
    this.zoomTransform = `scale(${Math.pow(2, this.zoomExp)})`;
  }

  @HostListener("document:keydown.-", ["$event", "-1"])
  @HostListener("document:keydown.=", ["$event", "1"])
  public zoomWithKey (event : KeyboardEvent, direction : number) : void
  {
    const keyZoomScaling : number = 0.1;
    this.changeZoom(direction * keyZoomScaling);
  }

  public zoom (event : WheelEvent) : void
  {
    const wheelZoomScaling : number = 0.02;
    this.changeZoom(event.deltaY * wheelZoomScaling);

    // Prevent actual page scrolling
    event.stopPropagation();
    event.preventDefault();
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/graphs/force-directed-graph.component.ts